import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useAppDispatch } from 'app/config/store';
import { getClasses, updateClasse } from 'app/entities/classe/classe.reducer';
import {
  createCycleSousMatiere,
  getCycleMatieres,
  getCycleScoringMode,
  getCycleSousMatieres,
  getEcoleMatieres,
  getCycleScoringConfiguration,
  saveCycleScoringConfiguration,
  CoefficientSource,
  ICycleMatiere,
  getCycles,
  saveCycleMatieres,
  updateCycleSousMatiere,
  deleteCycleSousMatiere,
} from 'app/entities/cycle/cycle.reducer';
import { ICycle } from 'app/shared/model/cycle.model';
import { IClasse } from 'app/shared/model/classe.model';
import {
  ClasseRow,
  CoefficientsState,
  CycleMatieresState,
  CycleRow,
  MatiereRow,
  ScoringModesByCycleState,
  SousMatiereRow,
  SousMatieresState,
  TMP_PREFIX,
  normalize,
  pairKey,
} from './pedagogie-wizard.types';
import { buildScoringConfiguration } from './scoring-configuration';

interface UsePedagogieWizardOptions {
  ecoleId: string;
  initialCycleId?: string;
  onBack: () => void;
}

export const usePedagogieWizard = ({ ecoleId, initialCycleId, onBack }: UsePedagogieWizardOptions) => {
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cycles, setCycles] = useState<CycleRow[]>([]);
  const [activeCycleId, setActiveCycleId] = useState<string | undefined>();
  const [classes, setClasses] = useState<ClasseRow[]>([]);
  const [selectedClasseIds, setSelectedClasseIds] = useState<string[]>([]);
  const [ecoleMatieres, setEcoleMatieres] = useState<ICycleMatiere[]>([]);
  const [cycleMatieres, setCycleMatieres] = useState<CycleMatieresState>({});
  const [sousMatieres, setSousMatieres] = useState<SousMatieresState>({});
  const [coefficients, setCoefficients] = useState<CoefficientsState>({});
  const [sousCoefficients, setSousCoefficients] = useState<CoefficientsState>({});
  const [coefficientSources, setCoefficientSources] = useState<Record<string, CoefficientSource>>({});
  const [savedCoefficientSources, setSavedCoefficientSources] = useState<Record<string, CoefficientSource>>({});
  const [scoringModesByCycle, setScoringModesByCycle] = useState<ScoringModesByCycleState>({});

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cycleResponse, classeResponse, matiereResponse] = await Promise.all([
        dispatch(getCycles(ecoleId)).unwrap(),
        dispatch(getClasses(ecoleId)).unwrap(),
        dispatch(getEcoleMatieres(ecoleId)).unwrap(),
      ]);

      const nextCycles: CycleRow[] = (cycleResponse.data || [])
        .filter((cycle): cycle is ICycle & { id: string } => Boolean(cycle.id))
        .map(cycle => ({
          ...cycle,
          localId: cycle.id,
          status: 'existing',
        }));
      const nextClasses: ClasseRow[] = (classeResponse.data || [])
        .filter((classe): classe is IClasse & { id: string } => Boolean(classe.id))
        .map(classe => ({
          ...classe,
          localId: classe.id,
          originalCycleId: classe.cycleId,
          status: 'existing',
        }));
      const nextCycleMatieres: CycleMatieresState = {};
      const nextSousMatieres: SousMatieresState = {};
      const nextCoefficients: CoefficientsState = {};
      const nextSousCoefficients: CoefficientsState = {};
      const nextCoefficientSources: Record<string, CoefficientSource> = {};
      const nextScoringModesByCycle: ScoringModesByCycleState = {};

      for (const cycle of nextCycles) {
        const [matieres, scoringMode, configuration] = await Promise.all([
          dispatch(getCycleMatieres(cycle.id)).unwrap(),
          dispatch(getCycleScoringMode(cycle.id)).unwrap(),
          dispatch(getCycleScoringConfiguration(cycle.id)).unwrap(),
        ]);
        nextScoringModesByCycle[cycle.id] = scoringMode;
        configuration.matieres.forEach(row => {
          nextCoefficientSources[row.cycleMatiereId] = row.coefficientSource;
        });
        configuration.coefficients.forEach(row => {
          nextCoefficients[row.matiereId] = { ...nextCoefficients[row.matiereId], [row.classeId]: row.coefficient };
        });
        configuration.sousMatiereCoefficients.forEach(row => {
          nextSousCoefficients[row.sousMatiereId] = { ...nextSousCoefficients[row.sousMatiereId], [row.classeId]: row.coefficient };
        });
        nextCycleMatieres[cycle.id] = (matieres || []).map(matiere => ({
          ...matiere,
          localId: matiere.id,
          cycleId: cycle.id,
          matiereId: matiere.id,
          status: 'existing',
        }));

        for (const matiere of matieres || []) {
          if (matiere.avecSousMatieres) {
            const rows = await dispatch(
              getCycleSousMatieres({ cycleMatiereId: matiere.cycleMatiereId, cycleId: cycle.id, matiereId: matiere.id })
            ).unwrap();
            nextSousMatieres[pairKey(cycle.id, matiere.id)] = (rows || []).map(row => ({
              ...row,
              localId: row.id,
              status: 'existing',
            }));
          }
        }
      }

      setCycles(nextCycles);
      setActiveCycleId(current =>
        initialCycleId && nextCycles.some(cycle => cycle.id === initialCycleId)
          ? initialCycleId
          : current && nextCycles.some(cycle => cycle.id === current)
          ? current
          : nextCycles[0]?.id
      );
      setClasses(nextClasses);
      setSelectedClasseIds(current => current.filter(id => nextClasses.some(classe => classe.id === id)));
      setEcoleMatieres(matiereResponse || []);
      setCycleMatieres(nextCycleMatieres);
      setSousMatieres(nextSousMatieres);
      setCoefficients(nextCoefficients);
      setSousCoefficients(nextSousCoefficients);
      setCoefficientSources(nextCoefficientSources);
      setSavedCoefficientSources(nextCoefficientSources);
      setScoringModesByCycle(nextScoringModesByCycle);
    } catch (error) {
      message.error('Impossible de charger la configuration pédagogique.');
    } finally {
      setLoading(false);
    }
  }, [dispatch, ecoleId, initialCycleId]);

  useEffect(() => {
    setCurrentStep(0);
    refreshAll();
  }, [refreshAll]);

  const existingCycleOptions = cycles.filter(cycle => cycle.id);
  const activeCycle = existingCycleOptions.find(cycle => cycle.id === activeCycleId);
  const classesByCycle = useMemo(
    () =>
      existingCycleOptions.reduce<Record<string, ClasseRow[]>>((acc, cycle) => {
        acc[cycle.id] = classes.filter(classe => classe.cycleId === cycle.id);
        return acc;
      }, {}),
    [classes, existingCycleOptions]
  );

  const activeCycleRecordId = activeCycle?.id;
  const matieresWithSousMatieres = useMemo(
    () => (activeCycleRecordId ? (cycleMatieres[activeCycleRecordId] || []).filter(matiere => matiere.id && matiere.avecSousMatieres) : []),
    [activeCycleRecordId, cycleMatieres]
  );
  const activeCycleClasses = useMemo(
    () => (activeCycleRecordId ? classes.filter(classe => classe.cycleId === activeCycleRecordId) : []),
    [activeCycleRecordId, classes]
  );
  const unlinkedClasses = classes.filter(classe => classe.status === 'existing' && !classe.cycleId);
  const classeStepRows = [...activeCycleClasses, ...unlinkedClasses];
  const activeCycleMatieres = activeCycleRecordId ? cycleMatieres[activeCycleRecordId] || [] : [];
  const activeCycleUsesCoefficients = activeCycleRecordId ? Boolean(scoringModesByCycle[activeCycleRecordId]?.usesCoefficients) : false;
  const lastStepIndex = activeCycleUsesCoefficients ? 4 : 3;

  useEffect(() => {
    setCurrentStep(step => Math.min(step, lastStepIndex));
  }, [lastStepIndex]);

  useEffect(() => {
    if (activeCycle?.id) {
      setSelectedClasseIds(activeCycleClasses.map(classe => classe.id).filter((id): id is string => Boolean(id)));
    }
  }, [activeCycle?.id, activeCycleClasses]);

  const saveClasses = async () => {
    const cycleId = activeCycle?.id;
    if (!cycleId) {
      message.warning("Sélectionnez d'abord un cycle.");
      return false;
    }
    const rowsToLink = unlinkedClasses.filter(classe => classe.id && selectedClasseIds.includes(classe.id));
    const rowsToUnlink = activeCycleClasses.filter(classe => classe.id && !selectedClasseIds.includes(classe.id));
    setSaving(true);
    try {
      for (const classe of rowsToLink) {
        await dispatch(updateClasse({ ...classe, cycleId, nom: classe.nom?.trim(), ecoleId })).unwrap();
      }
      for (const classe of rowsToUnlink) {
        await dispatch(updateClasse({ ...classe, cycleId: null, nom: classe.nom?.trim(), ecoleId })).unwrap();
      }
      await refreshAll();
      message.success('Classes enregistrées.');
      return true;
    } catch (error) {
      message.error("Impossible d'enregistrer les classes.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveMatieres = async () => {
    const cycleId = activeCycle?.id;
    if (!cycleId) {
      message.warning("Créez ou sélectionnez d'abord un cycle.");
      return false;
    }
    const allRows = cycleMatieres[cycleId] || [];
    const idsAndNames = allRows
      .filter(row => !row.deleted)
      .map(row => row.matiereId || normalize(row.nom))
      .filter(Boolean);
    if (new Set(idsAndNames).size !== idsAndNames.length) {
      message.warning(`Une matière est ajoutée deux fois dans ${activeCycle.nom}.`);
      return false;
    }

    const matieres = allRows.filter((row): row is MatiereRow & { matiereId: string } => !row.deleted && Boolean(row.matiereId));
    if (allRows.some(row => !row.deleted && !row.matiereId)) {
      message.warning('Une matière liée est invalide.');
      return false;
    }

    setSaving(true);
    try {
      await dispatch(
        saveCycleMatieres({
          cycleId,
          configuration: {
            matieres: matieres.map(row => ({
              matiereId: row.matiereId,
              avecSousMatieres: Boolean(row.avecSousMatieres),
            })),
          },
        })
      ).unwrap();
      await refreshAll();
      message.success('Matières enregistrées.');
      return true;
    } catch (error) {
      await refreshAll();
      message.error(
        error?.response?.data?.detail || error?.response?.data?.title || error?.message || "Impossible d'enregistrer les matières."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveSousMatieres = async () => {
    const cycleId = activeCycle?.id;
    const rows = Object.keys(sousMatieres)
      .filter(key => !cycleId || key.startsWith(`${cycleId}:`))
      .reduce<SousMatiereRow[]>((acc, key) => acc.concat(sousMatieres[key]), []);
    if (rows.some(row => !row.deleted && !row.nom?.trim())) {
      message.warning('Chaque sous-matière doit avoir un nom.');
      return false;
    }
    for (const key of Object.keys(sousMatieres).filter(itemKey => !cycleId || itemKey.startsWith(`${cycleId}:`))) {
      const names = sousMatieres[key].filter(row => !row.deleted).map(row => normalize(row.nom));
      if (new Set(names).size !== names.length) {
        message.warning('Deux sous-matières portent le même nom pour une matière.');
        return false;
      }
    }
    setSaving(true);
    try {
      for (const row of rows) {
        if (row.deleted && row.status === 'existing') {
          if (row.id.startsWith(TMP_PREFIX)) {
            message.warning('Une sous-matière temporaire ne peut pas être supprimée côté serveur.');
            return false;
          }
          await dispatch(deleteCycleSousMatiere(row.id)).unwrap();
        } else if (!row.deleted && row.status === 'new') {
          if (!row.cycleMatiereId || row.cycleMatiereId.startsWith(TMP_PREFIX)) {
            message.warning("Enregistrez d'abord la matière avant d'ajouter ses sous-matières.");
            return false;
          }
          await dispatch(createCycleSousMatiere({ nom: row.nom.trim(), cycleMatiereId: row.cycleMatiereId })).unwrap();
        } else if (!row.deleted && row.status === 'existing') {
          if (row.id.startsWith(TMP_PREFIX) || row.cycleMatiereId?.startsWith(TMP_PREFIX)) {
            message.warning('Une sous-matière temporaire ne peut pas être envoyée au serveur.');
            return false;
          }
          await dispatch(updateCycleSousMatiere({ ...row, nom: row.nom.trim() })).unwrap();
        }
      }
      await refreshAll();
      message.success('Sous-matières enregistrées.');
      return true;
    } catch (error) {
      message.error("Impossible d'enregistrer les sous-matières.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveCoefficients = async () => {
    setSaving(true);
    try {
      const configuration = buildScoringConfiguration(
        activeCycleMatieres,
        activeCycleClasses,
        sousMatieres,
        coefficientSources,
        coefficients,
        sousCoefficients
      );
      await dispatch(saveCycleScoringConfiguration({ cycleId: activeCycleRecordId, configuration })).unwrap();
      await refreshAll();
      message.success('Coefficients enregistrés.');
      return true;
    } catch (error) {
      setCoefficientSources(savedCoefficientSources);
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.title ||
          error?.message ||
          "Impossible d'enregistrer les coefficients. La stratégie enregistrée reste inchangée."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    const savers = activeCycleUsesCoefficients
      ? [saveClasses, saveMatieres, saveSousMatieres, saveCoefficients]
      : [saveClasses, saveMatieres, saveSousMatieres];
    const save = savers[currentStep];
    const ok = save ? await save() : true;
    if (ok) {
      setCurrentStep(step => Math.min(step + 1, lastStepIndex));
    }
  };

  return {
    activeCycle,
    activeCycleClasses,
    activeCycleId,
    activeCycleMatieres,
    activeCycleUsesCoefficients,
    classeStepRows,
    classesByCycle,
    coefficients,
    sousCoefficients,
    setSousCoefficients,
    coefficientSources,
    setCoefficientSources,
    currentStep,
    cycleMatieres,
    cycles,
    ecoleMatieres,
    existingCycleOptions,
    goNext,
    lastStepIndex,
    loading,
    matieresWithSousMatieres,
    onBack,
    saving,
    selectedClasseIds,
    scoringModesByCycle,
    setActiveCycleId,
    setClasses,
    setCoefficients,
    setCurrentStep,
    setCycleMatieres,
    setSelectedClasseIds,
    setSousMatieres,
    sousMatieres,
  };
};
